import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatTimestamp, isGuid } from "./adminUtils";
import {
  customSendAdminEmail,
  getAdminEmailTemplate,
  getAdminEmailTemplates,
  previewAdminCustomEmail,
  previewAdminEmailTemplate,
  sendAdminEmailTemplate,
  testSendAdminEmailTemplate,
  updateAdminEmailTemplate,
} from "../../api/adminEmailTemplates";

const MAX_ATTACHMENT_BYTES = 2_000_000;
const MAX_ATTACHMENT_COUNT = 3;
const LANGUAGE_PREF_STORAGE_KEY = "wayli.emailTemplateLanguages";

const DEFAULT_CUSTOM_FORM = {
  recipientType: "email",
  toEmail: "",
  userId: "",
  bookingId: "",
  subject: "",
  body: "",
  language: "en",
  attachments: [],
};

const DEFAULT_SEND_FORM = {
  recipientType: "email",
  toEmail: "",
  userId: "",
  bookingId: "",
};

const readLanguagePreferences = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(LANGUAGE_PREF_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const persistLanguagePreferences = (next) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LANGUAGE_PREF_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage errors.
  }
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const base64 = result.includes(",") ? result.split(",")[1] : "";
      resolve(base64);
    };
    reader.onerror = () => {
      reject(reader.error || new Error("Unable to read attachment."));
    };
    reader.readAsDataURL(file);
  });

const AdminEmailTemplatesPanel = ({ showFeedback, refreshAudit }) => {
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [templateActionId, setTemplateActionId] = useState("");
  const [languagePreferences, setLanguagePreferences] = useState(() =>
    readLanguagePreferences()
  );

  const [editorOpen, setEditorOpen] = useState(false);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [templateDetailLoading, setTemplateDetailLoading] = useState(false);
  const [templateDetailError, setTemplateDetailError] = useState("");
  const [editorLanguage, setEditorLanguage] = useState("en");
  const [editorDrafts, setEditorDrafts] = useState({});
  const [editorEnabled, setEditorEnabled] = useState(true);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);

  const [previewState, setPreviewState] = useState({
    subject: "",
    htmlBody: "",
    textBody: "",
    isLoading: false,
    error: "",
  });

  const [testAddress, setTestAddress] = useState("");
  const [testSending, setTestSending] = useState(false);

  const [sendNowForm, setSendNowForm] = useState({ ...DEFAULT_SEND_FORM });
  const [sendNowSending, setSendNowSending] = useState(false);

  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState({ ...DEFAULT_CUSTOM_FORM });
  const [customSending, setCustomSending] = useState(false);
  const [customPreviewState, setCustomPreviewState] = useState({
    subject: "",
    htmlBody: "",
    textBody: "",
    isLoading: false,
    error: "",
  });

  const availableLanguages = useMemo(() => {
    const langs = new Set();
    templates.forEach((template) => {
      (template.languages || []).forEach((lang) => langs.add(lang));
    });
    if (langs.size === 0) {
      langs.add("en");
    }
    return Array.from(langs).sort();
  }, [templates]);

  const editorLanguages = useMemo(() => {
    if (templateDetail?.variants?.length) {
      return templateDetail.variants.map((variant) => variant.language);
    }
    return availableLanguages;
  }, [templateDetail, availableLanguages]);

  const requiresBookingContext = useMemo(() => {
    const tokens = templateDetail?.supportedTokens || [];
    return tokens.some((token) => {
      const normalized = token.toLowerCase();
      return (
        normalized.startsWith("booking.") ||
        normalized.startsWith("location.") ||
        normalized.startsWith("equipment.")
      );
    });
  }, [templateDetail]);

  const currentDraft = editorDrafts[editorLanguage] || { subject: "", body: "" };

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError("");
    try {
      const payload = await getAdminEmailTemplates();
      const nextTemplates = Array.isArray(payload) ? payload : [];
      setTemplates(nextTemplates);
      if (nextTemplates.length > 0) {
        const nextPreferences = nextTemplates.reduce((acc, template) => {
          if (template?.type && template?.defaultLanguage) {
            acc[template.type] = template.defaultLanguage;
          }
          return acc;
        }, {});
        if (Object.keys(nextPreferences).length > 0) {
          setLanguagePreferences((prev) => {
            const merged = { ...prev, ...nextPreferences };
            persistLanguagePreferences(merged);
            return merged;
          });
        }
      }
    } catch (error) {
      setTemplatesError(error?.message || "Failed to load email templates.");
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleLanguagePreference = useCallback(
    async (templateType, language) => {
      if (!templateType || !language) return;
      setTemplateActionId(templateType);
      try {
        const response = await updateAdminEmailTemplate(templateType, {
          defaultLanguage: language,
          variants: [],
        });
        setTemplates((prev) =>
          prev.map((item) =>
            item.type === response.type
              ? {
                  ...item,
                  defaultLanguage: response.defaultLanguage || item.defaultLanguage,
                  isEnabled: response.isEnabled,
                  updatedUtc: response.updatedUtc,
                  languages: response.variants?.map((variant) => variant.language) || item.languages,
                  supportedTokens: response.supportedTokens || item.supportedTokens,
                  name: response.name || item.name,
                }
              : item
          )
        );
        if (templateDetail?.type === response.type) {
          setTemplateDetail(response);
          setEditorEnabled(Boolean(response.isEnabled));
        }
        setLanguagePreferences((prev) => {
          const next = { ...prev, [templateType]: language };
          persistLanguagePreferences(next);
          return next;
        });
      } catch (error) {
        showFeedback(error?.message || "Unable to update template language.", "warning");
      } finally {
        setTemplateActionId("");
      }
    },
    [showFeedback, templateDetail?.type]
  );

  const handleToggleTemplate = async (template) => {
    if (!template?.type) return;
    setTemplateActionId(template.type);
    try {
      const response = await updateAdminEmailTemplate(template.type, {
        isEnabled: !template.isEnabled,
        variants: [],
      });
      setTemplates((prev) =>
        prev.map((item) =>
          item.type === template.type
            ? {
                ...item,
                isEnabled: response.isEnabled,
                updatedUtc: response.updatedUtc,
                defaultLanguage: response.defaultLanguage || item.defaultLanguage,
                languages: response.variants?.map((variant) => variant.language) || item.languages,
                supportedTokens: response.supportedTokens || item.supportedTokens,
                name: response.name || item.name,
              }
            : item
        )
      );
      if (templateDetail?.type === template.type) {
        setTemplateDetail(response);
        setEditorEnabled(response.isEnabled);
      }
      if (refreshAudit) {
        refreshAudit({ silent: true });
      }
    } catch (error) {
      showFeedback(error?.message || "Unable to update template status.", "warning");
    } finally {
      setTemplateActionId("");
    }
  };

  const handleOpenEditor = async (templateType) => {
    if (!templateType) return;
    setEditorOpen(true);
    setTemplateDetailLoading(true);
    setTemplateDetailError("");
    setPreviewState({ subject: "", htmlBody: "", textBody: "", isLoading: false, error: "" });
    setEditorDirty(false);
    try {
      const detail = await getAdminEmailTemplate(templateType);
      setTemplateDetail(detail);
      setEditorEnabled(Boolean(detail?.isEnabled));
      const drafts = {};
      (detail?.variants || []).forEach((variant) => {
        drafts[variant.language] = {
          subject: variant.subject || "",
          body: variant.body || "",
        };
      });
      setEditorDrafts(drafts);
      const preferredLanguage = detail?.defaultLanguage || languagePreferences?.[templateType];
      const hasPreferred = detail?.variants?.some(
        (variant) => variant.language === preferredLanguage
      );
      const initialLanguage =
        (hasPreferred ? preferredLanguage : detail?.variants?.[0]?.language) ||
        availableLanguages[0] ||
        "en";
      setEditorLanguage(initialLanguage);
      setTestAddress("");
    } catch (error) {
      setTemplateDetailError(error?.message || "Failed to load template.");
      setTemplateDetail(null);
    } finally {
      setTemplateDetailLoading(false);
    }
  };

  const handleCloseEditor = useCallback(() => {
    setEditorOpen(false);
    setTemplateDetail(null);
    setTemplateDetailError("");
    setEditorDrafts({});
    setEditorEnabled(true);
    setEditorLanguage("en");
    setEditorDirty(false);
    setPreviewState({ subject: "", htmlBody: "", textBody: "", isLoading: false, error: "" });
    setTestAddress("");
    setSendNowForm({ ...DEFAULT_SEND_FORM });
  }, []);

  useEffect(() => {
    if (!editorOpen || !templateDetail?.type) return;
    setSendNowForm({
      ...DEFAULT_SEND_FORM,
      recipientType: requiresBookingContext ? "booking" : "email",
    });
  }, [editorOpen, templateDetail?.type, requiresBookingContext]);

  const handleDraftChange = (field) => (event) => {
    const value = event.target.value;
    setEditorDrafts((prev) => ({
      ...prev,
      [editorLanguage]: { ...prev[editorLanguage], [field]: value },
    }));
    setEditorDirty(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateDetail?.type) return;
    const variants = Object.entries(editorDrafts).map(([language, draft]) => ({
      language,
      subject: draft?.subject || "",
      body: draft?.body || "",
    }));
    if (variants.some((variant) => !variant.subject.trim() || !variant.body.trim())) {
      showFeedback("Subject and body are required for each language.", "warning");
      return;
    }
    setEditorSaving(true);
    try {
      const response = await updateAdminEmailTemplate(templateDetail.type, {
        isEnabled: editorEnabled,
        variants,
      });
      setTemplateDetail(response);
      setEditorDirty(false);
      setTemplates((prev) =>
        prev.map((item) =>
          item.type === response.type
            ? {
                ...item,
                isEnabled: response.isEnabled,
                updatedUtc: response.updatedUtc,
                defaultLanguage: response.defaultLanguage || item.defaultLanguage,
                languages: response.variants?.map((variant) => variant.language) || item.languages,
                supportedTokens: response.supportedTokens || item.supportedTokens,
                name: response.name || item.name,
              }
            : item
        )
      );
      showFeedback("Email template updated.", "success");
      if (refreshAudit) {
        refreshAudit({ silent: true });
      }
      await handlePreview();
    } catch (error) {
      showFeedback(error?.message || "Unable to save template.", "warning");
    } finally {
      setEditorSaving(false);
    }
  };

  const handlePreview = useCallback(async () => {
    if (!templateDetail?.type) return;
    setPreviewState((prev) => ({ ...prev, isLoading: true, error: "" }));
    try {
      const response = await previewAdminEmailTemplate({
        type: templateDetail.type,
        language: editorLanguage,
      });
      setPreviewState({
        subject: response.subject,
        htmlBody: response.htmlBody,
        textBody: response.textBody,
        isLoading: false,
        error: "",
      });
    } catch (error) {
      setPreviewState((prev) => ({
        ...prev,
        isLoading: false,
        error: error?.message || "Failed to load preview.",
      }));
    }
  }, [templateDetail, editorLanguage]);

  useEffect(() => {
    if (!editorOpen || !templateDetail?.type) return;
    handlePreview();
  }, [editorOpen, templateDetail?.type, editorLanguage, handlePreview]);

  const handleTestSend = async () => {
    if (!templateDetail?.type) return;
    if (!testAddress.trim()) {
      showFeedback("Enter a test email address.", "warning");
      return;
    }
    setTestSending(true);
    try {
      await testSendAdminEmailTemplate({
        type: templateDetail.type,
        language: editorLanguage,
        toEmail: testAddress.trim(),
      });
      showFeedback("Test email sent.", "success");
      if (refreshAudit) {
        refreshAudit({ silent: true });
      }
    } catch (error) {
      showFeedback(error?.message || "Unable to send test email.", "warning");
    } finally {
      setTestSending(false);
    }
  };

  const handleSendNowFieldChange = (field) => (event) => {
    const value = event.target.value;
    setSendNowForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendNowRecipientChange = (value) => {
    setSendNowForm((prev) => ({ ...prev, recipientType: value }));
  };

  const resolveSendNowRecipientPayload = () => {
    if (sendNowForm.recipientType === "email") {
      return { toEmail: sendNowForm.toEmail.trim() };
    }
    if (sendNowForm.recipientType === "user") {
      return { userId: sendNowForm.userId.trim() };
    }
    if (sendNowForm.recipientType === "booking") {
      return { bookingId: sendNowForm.bookingId.trim() };
    }
    return {};
  };

  const handleSendNow = async () => {
    if (!templateDetail?.type) return;
    const bookingId = sendNowForm.bookingId.trim();
    if (requiresBookingContext) {
      if (!bookingId) {
        showFeedback("Enter a booking ID to send this template.", "warning");
        return;
      }
      if (!isGuid(bookingId)) {
        showFeedback("Booking ID must be a valid GUID.", "warning");
        return;
      }
    }

    const recipientPayload = resolveSendNowRecipientPayload();
    if (!requiresBookingContext) {
      if (sendNowForm.recipientType === "email") {
        if (!recipientPayload.toEmail) {
          showFeedback("Enter an email address.", "warning");
          return;
        }
      } else if (sendNowForm.recipientType === "user") {
        if (!recipientPayload.userId) {
          showFeedback("Enter a user ID.", "warning");
          return;
        }
        if (!isGuid(recipientPayload.userId)) {
          showFeedback("User ID must be a valid GUID.", "warning");
          return;
        }
      } else if (sendNowForm.recipientType === "booking") {
        if (!recipientPayload.bookingId) {
          showFeedback("Enter a booking ID.", "warning");
          return;
        }
        if (!isGuid(recipientPayload.bookingId)) {
          showFeedback("Booking ID must be a valid GUID.", "warning");
          return;
        }
      }
    }

    setSendNowSending(true);
    try {
      await sendAdminEmailTemplate({
        type: templateDetail.type,
        language: editorLanguage,
        bookingId: requiresBookingContext ? bookingId : undefined,
        ...recipientPayload,
      });
      showFeedback("Email sent.", "success");
      if (refreshAudit) {
        refreshAudit({ silent: true });
      }
    } catch (error) {
      showFeedback(error?.message || "Unable to send email.", "warning");
    } finally {
      setSendNowSending(false);
    }
  };

  const handleOpenCustom = () => {
    setCustomOpen(true);
    setCustomForm((prev) => ({
      ...DEFAULT_CUSTOM_FORM,
      language: prev.language || "en",
    }));
    setCustomPreviewState({
      subject: "",
      htmlBody: "",
      textBody: "",
      isLoading: false,
      error: "",
    });
  };

  const handleCloseCustom = useCallback(() => {
    setCustomOpen(false);
    setCustomForm({ ...DEFAULT_CUSTOM_FORM });
    setCustomPreviewState({
      subject: "",
      htmlBody: "",
      textBody: "",
      isLoading: false,
      error: "",
    });
  }, []);

  useEffect(() => {
    if (!editorOpen && !customOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (editorOpen) {
        handleCloseEditor();
      }
      if (customOpen) {
        handleCloseCustom();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [customOpen, editorOpen, handleCloseCustom, handleCloseEditor]);

  const handleCustomFieldChange = (field) => (event) => {
    const value = event.target.value;
    setCustomForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomRecipientChange = (value) => {
    setCustomForm((prev) => ({
      ...prev,
      recipientType: value,
    }));
  };

  const handleCustomPreview = async () => {
    if (!customForm.subject.trim() || !customForm.body.trim()) {
      showFeedback("Subject and body are required.", "warning");
      return;
    }
    const userId = customForm.userId.trim();
    const bookingId = customForm.bookingId.trim();
    if (userId && !isGuid(userId)) {
      showFeedback("User ID must be a valid GUID.", "warning");
      return;
    }
    if (bookingId && !isGuid(bookingId)) {
      showFeedback("Booking ID must be a valid GUID.", "warning");
      return;
    }
    setCustomPreviewState((prev) => ({ ...prev, isLoading: true, error: "" }));
    try {
      const response = await previewAdminCustomEmail({
        language: customForm.language,
        subject: customForm.subject,
        body: customForm.body,
        context: {
          bookingId: bookingId || null,
          userId: userId || null,
        },
      });
      setCustomPreviewState({
        subject: response.subject,
        htmlBody: response.htmlBody,
        textBody: response.textBody,
        isLoading: false,
        error: "",
      });
    } catch (error) {
      setCustomPreviewState((prev) => ({
        ...prev,
        isLoading: false,
        error: error?.message || "Failed to load preview.",
      }));
    }
  };

  const handleCustomAttachments = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const remainingSlots = MAX_ATTACHMENT_COUNT - customForm.attachments.length;
    if (remainingSlots <= 0) {
      showFeedback(`Maximum ${MAX_ATTACHMENT_COUNT} attachments allowed.`, "warning");
      return;
    }
    const acceptedFiles = files.slice(0, remainingSlots);
    const uploads = [];
    for (const file of acceptedFiles) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        showFeedback(`${file.name} exceeds the size limit.`, "warning");
        continue;
      }
      try {
        const base64 = await readFileAsBase64(file);
        uploads.push({
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          base64Content: base64,
          size: file.size,
        });
      } catch (error) {
        showFeedback(error?.message || `Unable to read ${file.name}.`, "warning");
      }
    }
    if (uploads.length) {
      setCustomForm((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...uploads],
      }));
    }
  };

  const handleRemoveAttachment = (fileName) => {
    setCustomForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((item) => item.fileName !== fileName),
    }));
  };

  const resolveCustomRecipientPayload = () => {
    if (customForm.recipientType === "email") {
      return { toEmail: customForm.toEmail.trim() };
    }
    if (customForm.recipientType === "user") {
      return { userId: customForm.userId.trim() };
    }
    if (customForm.recipientType === "booking") {
      return { bookingId: customForm.bookingId.trim() };
    }
    return {};
  };

  const handleCustomSend = async () => {
    if (!customForm.subject.trim() || !customForm.body.trim()) {
      showFeedback("Subject and body are required.", "warning");
      return;
    }
    const recipientPayload = resolveCustomRecipientPayload();
    if (customForm.recipientType === "email") {
      if (!recipientPayload.toEmail) {
        showFeedback("Enter an email address.", "warning");
        return;
      }
    } else if (customForm.recipientType === "user") {
      if (!recipientPayload.userId) {
        showFeedback("Enter a user ID.", "warning");
        return;
      }
      if (!isGuid(recipientPayload.userId)) {
        showFeedback("User ID must be a valid GUID.", "warning");
        return;
      }
    } else if (customForm.recipientType === "booking") {
      if (!recipientPayload.bookingId) {
        showFeedback("Enter a booking ID.", "warning");
        return;
      }
      if (!isGuid(recipientPayload.bookingId)) {
        showFeedback("Booking ID must be a valid GUID.", "warning");
        return;
      }
    }
    setCustomSending(true);
    try {
      await customSendAdminEmail({
        language: customForm.language,
        subject: customForm.subject,
        body: customForm.body,
        attachments: customForm.attachments.map((item) => ({
          fileName: item.fileName,
          contentType: item.contentType,
          base64Content: item.base64Content,
        })),
        ...recipientPayload,
      });
      showFeedback("Custom email sent.", "success");
      if (refreshAudit) {
        refreshAudit({ silent: true });
      }
      handleCloseCustom();
    } catch (error) {
      showFeedback(error?.message || "Unable to send custom email.", "warning");
    } finally {
      setCustomSending(false);
    }
  };

  return (
    <>
      <article
        id="settings-card-email-templates"
        className="admin-settings__card admin-settings__card--full"
      >
        <div className="admin-settings__card-head admin-email-templates__head">
          <div className="admin-email-templates__head-text">
            <h3>Email templates</h3>
          </div>
          <button
            type="button"
            className="admin-button admin-button--outline"
            onClick={handleOpenCustom}
          >
            Email
          </button>
        </div>
        <div className="admin-settings__card-body">
          {templatesLoading ? (
            <p className="admin-item__note">Loading templates...</p>
          ) : templatesError ? (
            <p className="admin-settings__error">{templatesError}</p>
          ) : templates.length === 0 ? (
            <p className="admin-item__note">No templates found.</p>
          ) : (
            <ul className="admin-list admin-list--compact admin-email-templates__list">
              {templates.map((template) => (
                <li key={template.type} className="admin-item admin-email-templates__item">
                  <div className="admin-item__header">
                    <div>
                      <h4 className="admin-email-templates__title">{template.name}</h4>
                      <p className="admin-email-templates__meta">
                        Last updated {formatTimestamp(template.updatedUtc)}
                      </p>
                    </div>
                    <div className="admin-email-templates__actions">
                      <button
                        type="button"
                        className={`admin-toggle ${
                          template.isEnabled ? "admin-toggle--active" : ""
                        }`}
                        onClick={() => handleToggleTemplate(template)}
                        disabled={templateActionId === template.type}
                      >
                        {template.isEnabled ? "Enabled" : "Disabled"}
                      </button>
                      <button
                        type="button"
                        className="admin-button admin-button--outline"
                        onClick={() => handleOpenEditor(template.type)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                  <div className="admin-item__footer">
                    <div className="admin-tags">
                      {(template.languages || []).map((language) => {
                        const isPreferred =
                          (template.defaultLanguage || languagePreferences?.[template.type]) ===
                          language;
                        return (
                          <button
                            key={`${template.type}-${language}`}
                            type="button"
                            className={`admin-tag admin-tag--button ${
                              isPreferred ? "admin-tag--active" : ""
                            }`.trim()}
                            onClick={() => handleLanguagePreference(template.type, language)}
                          >
                            {language.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                    <p className="admin-item__note">
                      {template.supportedTokens?.length || 0} tokens
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </article>

      <div
        className={`admin-drawer__scrim ${
          editorOpen ? "admin-drawer__scrim--visible" : ""
        }`}
        onClick={handleCloseEditor}
        aria-hidden={!editorOpen}
      />
      <aside
        className={`admin-drawer admin-drawer--email ${
          editorOpen ? "admin-drawer--open" : ""
        }`.trim()}
        aria-hidden={!editorOpen}
        aria-labelledby="emailTemplateDrawerTitle"
      >
        <header className="admin-drawer__header">
          <div>
            <p className="admin-drawer__eyebrow">Email template</p>
            <h2 id="emailTemplateDrawerTitle" className="admin-drawer__title">
              {templateDetail?.name || "Email template"}
            </h2>
            <p className="admin-drawer__subtitle">
              {templateDetail?.type || "Loading template"}
            </p>
          </div>
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={handleCloseEditor}
          >
            Close
          </button>
        </header>
        <div className="admin-drawer__body">
          {templateDetailLoading ? (
            <p className="admin-item__note">Loading template details...</p>
          ) : templateDetailError ? (
            <p className="admin-settings__error">{templateDetailError}</p>
          ) : templateDetail ? (
            <>
              <section className="admin-email-templates__section">
                <div className="admin-email-templates__section-head">
                  <h4>Content</h4>
                  <div className="admin-email-templates__section-actions">
                    <button
                      type="button"
                      className={`admin-toggle ${editorEnabled ? "admin-toggle--active" : ""}`}
                      onClick={() => {
                        setEditorEnabled((prev) => !prev);
                        setEditorDirty(true);
                      }}
                    >
                      {editorEnabled ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                </div>
                <div className="admin-form__row">
                  <label className="admin-form__label">Language</label>
                  <select
                    className="admin-select"
                    value={editorLanguage}
                    onChange={(event) => setEditorLanguage(event.target.value)}
                  >
                    {editorLanguages.map((language) => (
                      <option key={`${templateDetail.type}-${language}`} value={language}>
                        {language.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-form__row">
                  <label className="admin-form__label">Subject</label>
                  <input
                    className="admin-input"
                    value={currentDraft.subject}
                    onChange={handleDraftChange("subject")}
                  />
                </div>
                <div className="admin-form__row">
                  <label className="admin-form__label">Body</label>
                  <textarea
                    className="admin-input admin-input--textarea"
                    value={currentDraft.body}
                    onChange={handleDraftChange("body")}
                  />
                </div>
                <p className="admin-item__note">
                  Preview uses the saved template content. Save to refresh the preview.
                </p>
              </section>

              <section className="admin-email-templates__section">
                <div className="admin-email-templates__section-head">
                  <h4>Tokens</h4>
                </div>
                <div className="admin-tags">
                  {(templateDetail.supportedTokens || []).map((token) => (
                    <span key={`${templateDetail.type}-${token}`} className="admin-tag">
                      {`{{${token}}}`}
                    </span>
                  ))}
                </div>
              </section>

              <section className="admin-email-templates__section">
                <div className="admin-email-templates__preview-head">
                  <h4>Preview</h4>
                  <button
                    type="button"
                    className="admin-button admin-button--outline"
                    onClick={handlePreview}
                    disabled={previewState.isLoading}
                  >
                    Refresh preview
                  </button>
                </div>
                {previewState.error ? (
                  <p className="admin-settings__error">{previewState.error}</p>
                ) : previewState.isLoading ? (
                  <p className="admin-item__note">Rendering preview...</p>
                ) : (
                  <div className="admin-email-templates__preview">
                    <p className="admin-email-templates__preview-subject">
                      {previewState.subject || "Preview subject"}
                    </p>
                    <iframe
                      className="admin-email-templates__preview-frame"
                      title="Email preview"
                      srcDoc={previewState.htmlBody || ""}
                    />
                  </div>
                )}
              </section>

              <section className="admin-email-templates__section">
                <div className="admin-email-templates__section-head">
                  <h4>Send test</h4>
                </div>
                <div className="admin-form__inline">
                  <input
                    className="admin-input"
                    value={testAddress}
                    onChange={(event) => setTestAddress(event.target.value)}
                    placeholder="name@company.com"
                  />
                  <button
                    type="button"
                    className="admin-button"
                    onClick={handleTestSend}
                    disabled={testSending}
                  >
                    {testSending ? "Sending..." : "Send test"}
                  </button>
                </div>
              </section>

              <section className="admin-email-templates__section">
                <div className="admin-email-templates__section-head">
                  <h4>Send now</h4>
                </div>
                {requiresBookingContext ? (
                  <>
                    <div className="admin-form__row">
                      <label className="admin-form__label">Booking ID</label>
                      <input
                        className="admin-input"
                        value={sendNowForm.bookingId}
                        onChange={handleSendNowFieldChange("bookingId")}
                        placeholder="00000000-0000-0000-0000-000000000000"
                      />
                    </div>
                    <p className="admin-item__note">
                      Sends to the booking contact email and includes booking details.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="admin-settings__pill-group admin-email-templates__pill-group">
                      <label>
                        <input
                          type="radio"
                          checked={sendNowForm.recipientType === "email"}
                          onChange={() => handleSendNowRecipientChange("email")}
                        />
                        Email address
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={sendNowForm.recipientType === "user"}
                          onChange={() => handleSendNowRecipientChange("user")}
                        />
                        User ID
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={sendNowForm.recipientType === "booking"}
                          onChange={() => handleSendNowRecipientChange("booking")}
                        />
                        Booking ID
                      </label>
                    </div>
                    {sendNowForm.recipientType === "email" && (
                      <div className="admin-form__row">
                        <label className="admin-form__label">Email</label>
                        <input
                          className="admin-input"
                          value={sendNowForm.toEmail}
                          onChange={handleSendNowFieldChange("toEmail")}
                        />
                      </div>
                    )}
                    {sendNowForm.recipientType === "user" && (
                      <div className="admin-form__row">
                        <label className="admin-form__label">User ID</label>
                        <input
                          className="admin-input"
                          value={sendNowForm.userId}
                          onChange={handleSendNowFieldChange("userId")}
                          placeholder="00000000-0000-0000-0000-000000000000"
                        />
                      </div>
                    )}
                    {sendNowForm.recipientType === "booking" && (
                      <div className="admin-form__row">
                        <label className="admin-form__label">Booking ID</label>
                        <input
                          className="admin-input"
                          value={sendNowForm.bookingId}
                          onChange={handleSendNowFieldChange("bookingId")}
                          placeholder="00000000-0000-0000-0000-000000000000"
                        />
                      </div>
                    )}
                  </>
                )}
                {!editorEnabled && (
                  <p className="admin-item__note">Enable this template to send it manually.</p>
                )}
                <div className="admin-form__actions">
                  <button
                    type="button"
                    className="admin-button"
                    onClick={handleSendNow}
                    disabled={sendNowSending || !editorEnabled}
                  >
                    {sendNowSending ? "Sending..." : "Send now"}
                  </button>
                </div>
              </section>

              <div className="admin-form__actions">
                <button
                  type="button"
                  className="admin-button"
                  onClick={handleSaveTemplate}
                  disabled={editorSaving || !editorDirty}
                >
                  {editorSaving ? "Saving..." : "Save template"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </aside>

      <div
        className={`admin-drawer__scrim ${
          customOpen ? "admin-drawer__scrim--visible" : ""
        }`}
        onClick={handleCloseCustom}
        aria-hidden={!customOpen}
      />
      <aside
        className={`admin-drawer admin-drawer--email ${
          customOpen ? "admin-drawer--open" : ""
        }`.trim()}
        aria-hidden={!customOpen}
        aria-labelledby="customEmailDrawerTitle"
      >
        <header className="admin-drawer__header">
          <div>
            <p className="admin-drawer__eyebrow">Blank email</p>
            <h2 id="customEmailDrawerTitle" className="admin-drawer__title">
              Compose blank email
            </h2>
            <p className="admin-drawer__subtitle">
              Write any message and keep the Wayli email layout.
            </p>
          </div>
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={handleCloseCustom}
          >
            Close
          </button>
        </header>
        <div className="admin-drawer__body">
          <section className="admin-email-templates__section">
            <div className="admin-email-templates__section-head">
              <h4>Recipients</h4>
            </div>
            <div className="admin-settings__pill-group admin-email-templates__pill-group">
              <label>
                <input
                  type="radio"
                  checked={customForm.recipientType === "email"}
                  onChange={() => handleCustomRecipientChange("email")}
                />
                Email address
              </label>
              <label>
                <input
                  type="radio"
                  checked={customForm.recipientType === "user"}
                  onChange={() => handleCustomRecipientChange("user")}
                />
                User ID
              </label>
              <label>
                <input
                  type="radio"
                  checked={customForm.recipientType === "booking"}
                  onChange={() => handleCustomRecipientChange("booking")}
                />
                Booking ID
              </label>
            </div>
            {customForm.recipientType === "email" && (
              <div className="admin-form__row">
                <label className="admin-form__label">Email</label>
                <input
                  className="admin-input"
                  value={customForm.toEmail}
                  onChange={handleCustomFieldChange("toEmail")}
                />
              </div>
            )}
            {customForm.recipientType === "user" && (
              <div className="admin-form__row">
                <label className="admin-form__label">User ID</label>
                <input
                  className="admin-input"
                  value={customForm.userId}
                  onChange={handleCustomFieldChange("userId")}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </div>
            )}
            {customForm.recipientType === "booking" && (
              <div className="admin-form__row">
                <label className="admin-form__label">Booking ID</label>
                <input
                  className="admin-input"
                  value={customForm.bookingId}
                  onChange={handleCustomFieldChange("bookingId")}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </div>
            )}
          </section>

          <section className="admin-email-templates__section">
            <div className="admin-email-templates__section-head">
              <h4>Content</h4>
            </div>
            <div className="admin-form__row">
              <label className="admin-form__label">Language</label>
              <select
                className="admin-select"
                value={customForm.language}
                onChange={handleCustomFieldChange("language")}
              >
                {availableLanguages.map((language) => (
                  <option key={`custom-${language}`} value={language}>
                    {language.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-form__row">
              <label className="admin-form__label">Subject</label>
              <input
                className="admin-input"
                value={customForm.subject}
                onChange={handleCustomFieldChange("subject")}
              />
            </div>
            <div className="admin-form__row">
              <label className="admin-form__label">Body</label>
              <textarea
                className="admin-input admin-input--textarea"
                value={customForm.body}
                onChange={handleCustomFieldChange("body")}
              />
            </div>
          </section>

          <section className="admin-email-templates__section">
            <div className="admin-email-templates__section-head">
              <h4>Attachments</h4>
            </div>
            <input
              type="file"
              className="admin-input"
              multiple
              onChange={handleCustomAttachments}
            />
            {customForm.attachments.length > 0 && (
              <ul className="admin-email-templates__attachments">
                {customForm.attachments.map((attachment) => (
                  <li key={attachment.fileName} className="admin-email-templates__attachment">
                    <span>{attachment.fileName}</span>
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={() => handleRemoveAttachment(attachment.fileName)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="admin-item__note">
              Up to {MAX_ATTACHMENT_COUNT} files. Max size {Math.round(
                MAX_ATTACHMENT_BYTES / 1_000_000
              )}
              MB each.
            </p>
          </section>

          <section className="admin-email-templates__section">
            <div className="admin-email-templates__preview-head">
              <h4>Preview</h4>
              <button
                type="button"
                className="admin-button admin-button--outline"
                onClick={handleCustomPreview}
                disabled={customPreviewState.isLoading}
              >
                Preview
              </button>
            </div>
            {customPreviewState.error ? (
              <p className="admin-settings__error">{customPreviewState.error}</p>
            ) : customPreviewState.isLoading ? (
              <p className="admin-item__note">Rendering preview...</p>
            ) : customPreviewState.htmlBody ? (
              <div className="admin-email-templates__preview">
                <p className="admin-email-templates__preview-subject">
                  {customPreviewState.subject || "Preview subject"}
                </p>
                <iframe
                  className="admin-email-templates__preview-frame"
                  title="Custom email preview"
                  srcDoc={customPreviewState.htmlBody || ""}
                />
              </div>
            ) : (
              <p className="admin-item__note">Generate a preview before sending.</p>
            )}
          </section>

          <div className="admin-form__actions">
            <button
              type="button"
              className="admin-button"
              onClick={handleCustomSend}
              disabled={customSending}
            >
              {customSending ? "Sending..." : "Send now"}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminEmailTemplatesPanel;
